import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import type { CedarPolicy } from '../../../types';

// Human-readable policy descriptions
const POLICY_DESCRIPTIONS: Record<string, { title: string; subtitle: string }> = {
  // Super Admin
  'msreportingadmin-full-access': {
    title: 'Super Admin - Full Access',
    subtitle: 'Complete access to all resources (Entra msreportingadmin role)',
  },
  'service-full-access': {
    title: 'Internal Services - Full Access',
    subtitle: 'Lambda-to-Lambda internal service calls',
  },

  // Agent
  'agent-full-scope': {
    title: 'Agent - Scoped Access',
    subtitle: 'AI agent access within assigned customer scope',
  },

  // Public policies
  'global-kb-read': {
    title: 'Public - Global Knowledge Base',
    subtitle: 'Everyone can read global knowledge bases',
  },
  'product-catalog-read': {
    title: 'Public - ServiceComponent Catalog',
    subtitle: 'Everyone can read the product catalog',
  },
  'playbook-read': {
    title: 'Public - Playbooks',
    subtitle: 'Everyone can read playbooks',
  },
};

const getPolicyDisplay = (policy: CedarPolicy) => {
  const description = policy.description ?? '';
  const mapped = POLICY_DESCRIPTIONS[description];
  if (mapped) {
    return mapped;
  }
  return {
    title: description || policy.policy_id,
    subtitle: 'Cedar Policy',
  };
};

interface PolicyViewerProps {
  policies: CedarPolicy[];
  selectedPolicy: CedarPolicy | null;
  onSelectPolicy: (policy: CedarPolicy) => void;
  loading: boolean;
  error: string | null;
}

const PolicyViewer: React.FC<PolicyViewerProps> = ({
  policies,
  selectedPolicy,
  onSelectPolicy,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <InlineAlert severity="error" sx={{ m: 2 }}>
        {error}
      </InlineAlert>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 400 }}>
      {/* Policy List */}
      <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
        <Typography variant="subtitle2" sx={{ p: 2, fontWeight: 600, bgcolor: 'action.hover' }}>
          Policies ({policies.length})
        </Typography>
        <Divider />
        <List dense disablePadding>
          {policies.map((policy) => (
            <ListItem key={policy.policy_id} disablePadding>
              <ListItemButton
                selected={selectedPolicy?.policy_id === policy.policy_id}
                onClick={() => onSelectPolicy(policy)}
              >
                <ListItemText
                  primary={getPolicyDisplay(policy).title}
                  secondary={getPolicyDisplay(policy).subtitle}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                  secondaryTypographyProps={{ noWrap: true, fontSize: '0.75rem', color: 'text.secondary' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Policy Details */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        {selectedPolicy ? (
          <>
            <Typography variant="h6" gutterBottom>
              {getPolicyDisplay(selectedPolicy).title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {getPolicyDisplay(selectedPolicy).subtitle}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 2 }}>
              Policy ID: {selectedPolicy.description}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'background.default',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {selectedPolicy.statement}
            </Paper>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary">
              Select a policy to view details
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PolicyViewer;
